import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Routes } from '../../classes/Routes';
import { AuthUser } from '../../classes/AuthUser';
import { IAppState } from '../../interfaces';
import { startImpersonation, stopImpersonation } from '../../store/actions/authActions';
import HeaderContext from '../../context/HeaderContext';
import { ETranslation } from '../../translations/translation-keys';
import classes from './Header.module.scss';
import Menu, { EMenuType, IMenuItem } from './Menu/Menu';
import ChangePasswordDialog from '../Auth/ChangePasswordDialog/ChangePasswordDialog';
import ModalContext, { defaultModal, EModalSize } from '../UI/Modal/ModalContext';

interface IProps extends RouteComponentProps {
  currentUser: AuthUser;
  logout: () => void;
}

const Header: React.FC<IProps> = ({ history, currentUser, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const realUser = useSelector((state: IAppState) => state.auth.realUser);
  const imitateSession = useSelector((state: IAppState) => state.auth.imitateSession);
  const isPrimaryAdmin = currentUser?.isPrimary || realUser?.isPrimary;

  const toggleMenu = () => setIsOpen((prevState) => !prevState);

  const { t } = useTranslation();

  const { setModal } = React.useContext(ModalContext);

  const clickHandler = (link: string) => history.push(link);

  const openChangePasswordModal = () => setModal({
    isOpen: true,
    title: t(ETranslation.CHANGE_PASSWORD),
    size: EModalSize.SMALL,
    content: <ChangePasswordDialog onCancel={() => setModal(defaultModal)} />,
  });

  const menuItems: IMenuItem[] = [
    { text: t(ETranslation.ORDER_SERVICE), to: Routes.SUBSCRIPTION_EXPIRED },
    { text: t(ETranslation.OFFERREQUESTS), to: Routes.OFFER_REQUESTS },
    { text: "Myyntiputki", to: Routes.CRM },
    { text: "Tarjoushistoria", to: Routes.OFFER_HISTORY },
    { text: t(ETranslation.REPORTS), to: Routes.REPORTS },
    { text: t(ETranslation.LOCATIONS), to: Routes.LOCATIONS },
    { text: t(ETranslation.SERVICEPROVIDERS), to: Routes.SERVICE_PROVIDERS },
    { text: t(ETranslation.SERVICES), to: Routes.SERVICES },
    { text: t(ETranslation.FEEDBACKS), to: Routes.FEEDBACKS },
    { text: t(ETranslation.OPERATORS), to: Routes.OPERATORS },
    { text: t(ETranslation.CONTENTS), to: Routes.CONTENTS },
    { text: t(ETranslation.MANAGEMENT), subMenuItems: [
        { text: "Promptit", to: Routes.PROMPT_FRAGMENTS },
        { text: "Markkinatutkimus", to: Routes.MARKET_RESEARCH_TASKS },
        { text: "Agent-tehtävät", to: Routes.AGENT_TASKS },
        { text: "Onboarding", to: Routes.ONBOARDING },
        { text: "Liidit", to: Routes.LEADS },
        { text: t(ETranslation.OFFER_REQUEST_ACCESS), to: Routes.OFFER_REQUEST_ACCESS },
        { text: t(ETranslation.AUDIT_TRAIL), to: Routes.AUDIT_TRAIL }
      ]
    },
    { text: t(ETranslation.COMMON_SEARCH), search: true },
  ];

  const userSubMenuItems: IMenuItem[] = [
    { text: t(ETranslation.ORGANIZATION_ME), to: Routes.ORGANIZATIONS_ME },
    { text: t(ETranslation.USERS), to: Routes.USERS },
    { text: t(ETranslation.CHANGE_PASSWORD), action: openChangePasswordModal },
    { text: t(ETranslation.SETTINGS), to: Routes.SETTINGS },
  ];

  if (isPrimaryAdmin) {
    if (imitateSession) {
      userSubMenuItems.push({
        text: "🎭 Palaa pääkäyttäjäksi",
        action: () => {
          dispatch(stopImpersonation());
          window.location.reload();
        }
      });
    } else {
      userSubMenuItems.push(
        {
          text: "🎭 Operaattori",
          action: () => {
            dispatch(startImpersonation({
              level: "OPERATOR",
              orgName: "Operaattorinäkymä",
              allowWrite: false,
            }));
            const forbiddenForOperator = [
              Routes.OPERATORS,
              Routes.OPERATOR,
              Routes.AUDIT_TRAIL,
              Routes.MARKET_RESEARCH_TASKS,
              Routes.MARKET_RESEARCH_TASK,
              Routes.ONBOARDING,
              Routes.LEADS,
              Routes.LEAD,
              Routes.CONTENTS,
              Routes.CONTENT,
              Routes.CONTENTS_CONTENT,
              Routes.PROMPT_FRAGMENTS,
              Routes.PROMPT_FRAGMENT,
              Routes.AGENT_TASKS,
              Routes.SERVICE('new')
            ];
            const currentPath = history.location.pathname;
            const isForbidden = forbiddenForOperator.some(path => currentPath === path || currentPath.startsWith(path + "/"));
            if (isForbidden) {
              history.push(Routes.OFFER_REQUESTS);
            }
          }
        },
        {
          text: "🎭 Palveluntarjoaja",
          action: () => {
            dispatch(startImpersonation({
              level: "SERVICE_PROVIDER",
              orgName: "Palveluntarjoajanäkymä",
              allowWrite: false,
              subscriptionLevel: "BASIC",
            }));
            const allowedForServiceProvider = [
              Routes.CRM,
              Routes.OFFER_REQUESTS,
              Routes.OFFER_REQUEST,
              Routes.OFFER_REQUEST_OFFER,
              Routes.OFFER_HISTORY,
              Routes.ORGANIZATIONS_ME
            ];
            const currentPath = history.location.pathname;
            const isAllowed = allowedForServiceProvider.some(path => currentPath === path || currentPath.startsWith(path + "/"));
            if (!isAllowed) {
              history.push(Routes.OFFER_REQUESTS);
            }
          }
        }
      );
    }
  }

  userSubMenuItems.push({ text: t(ETranslation.LOGOUT), action: logout });

  const dropdownMenuItems: IMenuItem[] = [
    { 
      text: currentUser?.name || "", 
      subMenuItems: userSubMenuItems
    }
  ];

  return (
    <HeaderContext.Provider value={{ menuItems, dropdownMenuItems }}>
      <Menu type={EMenuType.MOBILE} isOpen={isOpen} toggleMenu={toggleMenu} currentUser={currentUser}/>
      <header className={classes.Container}>
        <div className={classes.Wrapper}>
          <div className={classes.ToggleMenu} onClick={toggleMenu}>
            <FontAwesomeIcon icon={faBars} className={classes.Icon} />
          </div>
          <div className={classes.Logo} onClick={() => clickHandler(Routes.ADMIN)}>
          </div>
          <Menu type={EMenuType.DESKTOP} isOpen={false} toggleMenu={toggleMenu} currentUser={currentUser} />
        </div>
      </header>
    </HeaderContext.Provider>
  );
};

export default withRouter(React.memo(Header));
